import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from '@uhg-abyss/web/ui/Link';
import { styled } from '@uhg-abyss/web/tools/styled';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { event } from '@uhg-abyss/web/tools/event';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { Accordion } from '@uhg-abyss/web/ui/Accordion';
import marketingDetailsRoutes from '../../../Documentation/SideNav/SideNavLink/MarketingDetailsPageRoutes.json';
import { StyledLink } from '@src/common/Link';

const Wrapper = styled('div', {});

const StyledNavLink = styled(Link, {
  display: 'block',
  padding: '0px 12px 0px 12px',
  width: '100%',

  '&:hover': {
    backgroundColor: '#E5F8FB',
  },

  '&.abyss-link-active': {
    color: '#000000 !important',
    borderRight: ({ theme }) => {
      return `3px solid ${theme.colors.primary}`;
    },
    backgroundColor: '#E5F8FB !important',
  },
});

const LinkText = styled('div', {
  display: 'flex',
  padding: '4px 0px 4px 18px',
});

const StyledMethod = styled('span', {
  variants: {
    color: {
      GET: { color: '#007000', fontWeight: 'bold' },
      POST: { color: '#196ecf', fontWeight: 'bold' },
      PUT: { color: '#c40000', fontWeight: 'bold' },
      DELETE: { color: '#c40000', fontWeight: 'bold' },
    },
  },
});

const StyledSummary = styled('div', {
  paddingLeft: 5,
  width: '100%',
});

const NewBadge = styled('span', {
  color: '#ff681f',
  fontWeight: 'bold',
});

const PathSummary = styled('span', {
  paddingRight: '16px',
});

const StyledBackButton = styled(StyledLink, {
  margin: '0px 0px 2px 12px',
  padding: '16px 12px 0px 0px',
});

export const SideNavLinkProvider = ({
  service,
  data,
  currentTags,
  ...props
}) => {
  const { navigate } = useRouter();
  const [initialOpen, setInitialOpen] = useState([]);

  useEffect(() => {
    const arr = [];
    data.tagNames?.forEach((item, index) => {
      arr.push(item);
    });
    setInitialOpen(arr);
  }, [data]);

  const handleClick = () => {
    navigate(marketingDetailsRoutes[service]);
  };

  const fireInteractionEvent = (path) => {
    event('INTERACTION_EVENT', {
      interactionName: data?.info?.title,
      interactionValue: `${path?.method} ${path?.summary}`,
      interactionContext: 'left navigation link click',
    });
  };

  return (
    <Wrapper>
      <StyledBackButton
        onClick={handleClick}
        fontWeight="$bold"
        before={<IconSymbol icon="arrow_back" />}
      >
        Back to API Details
      </StyledBackButton>
      {service !== 'superconnector' && initialOpen && initialOpen.length ? (
        <Accordion
          defaultValue={initialOpen}
          isCollapsible
          type="multiple"
          expandAll
          css={{
            '.abyss-accordion-trigger': {
              display: 'flex',
              flexDirection: 'row-reverse',
              justifyContent: 'flex-end',
              borderBottom: 'none',
              fontSize: '15px !important',
              minHeight: '30px',
              color: '#000000',
              fontWeight: 'bold',
              paddingLeft: '12px',
            },
            '.abyss-accordion-header': {
              fontSize: '15px !important',
            },
            '.abyss-link-root': {
              padding: '0px',
              fontWeight: '$light',
            },
            '.abyss-accordion-content-text': {
              padding: '0px',
            },
          }}
        >
          {data?.pathsByTag?.map((tag, index) => {
            return (
              <Accordion.Item value={tag.tagName} key={tag.tagName}>
                <Accordion.Trigger>
                  <Accordion.Header>{tag.tagName}</Accordion.Header>
                </Accordion.Trigger>

                <Accordion.Content>
                  {tag.paths.map((path) => {
                    return (
                      <StyledNavLink
                        {...props}
                        key={path.operationId}
                        href={`/documentation/${service}/${path.operationSlug}`}
                        css={{ paddingLeft: '15px !important' }}
                        variant="custom"
                        fontWeight="$light"
                        onClick={() => {
                          return fireInteractionEvent(path);
                        }}
                      >
                        <LinkText style={{ fontSize: '15px' }}>
                          <StyledMethod color={path.method}>
                            {path.method}
                          </StyledMethod>
                          {path.summary?.includes('||') ? (
                            <StyledSummary>
                              <PathSummary>
                                {path.summary.split('||')[0]}
                              </PathSummary>

                              <NewBadge>{path.summary.split('||')[1]}</NewBadge>
                            </StyledSummary>
                          ) : (
                            <StyledSummary>{path.summary}</StyledSummary>
                          )}
                        </LinkText>
                      </StyledNavLink>
                    );
                  })}
                </Accordion.Content>
              </Accordion.Item>
            );
          })}
        </Accordion>
      ) : (
        data?.pathsByTag?.flatMap((tag) => {
          return tag.paths.map((path) => {
            return (
              <StyledNavLink
                {...props}
                fontWeight="$light"
                key={path.operationId}
                href={`/documentation/${service}/${path.operationSlug}`}
                variant="custom"
                onClick={() => {
                  return fireInteractionEvent(path);
                }}
              >
                <LinkText style={{ fontSize: '15px' }}>
                  <StyledMethod color={path.method}>{path.method}</StyledMethod>
                  {path.summary?.includes('||') ? (
                    <StyledSummary>
                      <PathSummary>{path.summary.split('||')[0]}</PathSummary>
                      <NewBadge>{path.summary.split('||')[1]}</NewBadge>
                    </StyledSummary>
                  ) : (
                    <StyledSummary>{path.summary}</StyledSummary>
                  )}
                </LinkText>
              </StyledNavLink>
            );
          });
        })
      )}
    </Wrapper>
  );
};

SideNavLinkProvider.propTypes = {
  service: PropTypes.string.isRequired,
  data: PropTypes.shape({
    paths: PropTypes.shape({}),
    pathsByTag: PropTypes.arrayOf(PropTypes.shape({})),
    tagNames: PropTypes.arrayOf(PropTypes.string),
    info: PropTypes.shape({}),
  }),
  currentTags: PropTypes.arrayOf(PropTypes.string),
};

SideNavLinkProvider.defaultProps = {
  data: {},
  currentTags: [],
};
